import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ShieldAlert, BadgeCheck, Mail, Clock } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function FreelancesAtRiskBlock() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/freelance/admin/at-risk").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);

  if (rows === null) return null;
  if (rows.length === 0) return null;

  const expired = rows.filter((r) => r.is_expired);
  const expiring = rows.filter((r) => !r.is_expired);

  return (
    <div className="bg-white border border-amber-300 rounded-lg p-6" data-testid="freelances-at-risk-block">
      <div className="flex items-center gap-3 mb-4">
        <ShieldAlert size={18} className="text-amber-700" />
        <h2 className="font-heading text-lg font-bold tracking-tight text-amber-900">Freelances at risk</h2>
        <span className="ml-auto text-xs font-mono text-amber-800 bg-amber-100 px-2 py-0.5 rounded">{rows.length}</span>
      </div>
      <p className="text-xs text-zinc-500 mb-4 max-w-2xl">
        Contrats freelance expireds ou expirant dans les 60 prochains jours. Cliquez sur un freelance pour ouvrir son profil et initier le renouvellement.
      </p>

      <div className="space-y-2">
        {expired.length > 0 && (
          <div className="text-[11px] uppercase tracking-[0.2em] text-red-700 font-mono mb-2">Expired</div>
        )}
        {expired.map((r) => (
          <Row key={r.user_id} r={r} variant="expired" />
        ))}

        {expiring.length > 0 && (
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-mono mt-4 mb-2">À renouveler</div>
        )}
        {expiring.map((r) => (
          <Row key={r.user_id} r={r} variant="expiring" />
        ))}
      </div>
    </div>
  );
}

function Row({ r, variant }) {
  const isExpired = variant === "expired";
  return (
    <Link
      to={`/admin/users`}
      data-testid={`at-risk-row-${r.user_id}`}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-md border transition-colors ${
        isExpired
          ? "bg-red-50 border-red-200 hover:bg-red-100"
          : "bg-amber-50/50 border-amber-200 hover:bg-amber-50"
      }`}
    >
      <BadgeCheck size={14} className={isExpired ? "text-red-700 shrink-0" : "text-amber-700 shrink-0"} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-zinc-900 truncate">{r.name}</div>
        <div className="text-[11px] font-mono text-zinc-500 truncate">{r.email} · {r.tjm_ht} {r.currency}/JH · {r.portage_company_name || "—"}</div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-xs font-mono ${isExpired ? "text-red-700" : "text-amber-800"}`}>
          {isExpired ? `Expiré il y a ${Math.abs(r.days_until_end)}j` : `J-${r.days_until_end}`}
        </div>
        <div className="text-[10px] text-zinc-500 font-mono mt-0.5">Fin {fmtDate(r.contract_end)}</div>
      </div>
      {r.renewal_email_sent_at && (
        <span title={`Email de renouvellement sent le ${fmtDate(r.renewal_email_sent_at)}`} className="text-emerald-600 shrink-0">
          <Mail size={12} />
        </span>
      )}
    </Link>
  );
}
